import { getRootOfNode, isChild } from '../../dux/modules/utils'
import { isConsumedId } from '../../dux/modules/sharedEndpoints'

// Mirrors folderConstants.ROOT_FOLDER_TYPE. Declared here rather than imported
// because dux/constants/folder.js imports this module — importing back would
// be circular.
const ROOT_FOLDER_TYPE = 'root'

/*
 * Exception function.  Validates whether a folder is a top-level folder by checking for
 * existence of parent folder
 */
export const isRoot = (folderId, allFolders) => {
  let isRoot = true
  if (allFolders[folderId] && allFolders[folderId].parentId) isRoot = false
  return isRoot
}

/*
 * Exception function.  Validates whether a folder is a child folder of the recycle bin
 * (can be a grandchild, etc)
 */
export const isInRecycleBin = (folderId, allFolders, constants) => {
  const binKeys = Object.keys(allFolders).filter(
    (folder) => allFolders[folder].type === constants.RECYCLE_BIN_FOLDER_TYPE,
  )
  // User can't see recycle bin!
  if (binKeys.length === 0) return false

  return isChild(folderId, binKeys[0], allFolders)
}

/*
 * Exception function.  Validates whether a folder is a webfolder root folder
 */
export const isWebRootFolder = (
  folderId,
  allFolders,
  constants,
  folderType,
) => {
  const folder = allFolders[folderId]
  return folderType === 'webfolder' && !folder.parentId
}

/*
 * Exception function.  Validates whether a folder is a shared-endpoint root folder
 */
export const isSharedRootFolder = (
  folderId,
  allFolders,
  constants,
  folderType,
) => {
  const folder = allFolders[folderId]
  return folderType === 'sharedfolder' && !folder.parentId
}

/*
 * Exception function.  Validates whether a folder is consumed shared-endpoint
 * content — syndicated from another BAM! instance and read-only here
 * (SHARED-ENDPOINTS-PLAN.md §4.5). Every folder-menu action is a mutation, so
 * this hides the menu entirely; the API would reject the write anyway.
 */
export const isConsumedFolder = (folderId, allFolders) => {
  if (isConsumedId(folderId)) return true
  const folder = allFolders && allFolders[folderId]
  return !!(folder && folder.sharedEndpoint)
}

export const isVirtual = (folderId, allFolders) => {
  return allFolders[folderId] && allFolders[folderId].virtual
}

/*
 * The Library endpoint's CONTENT root folder ids, taken from the LIB endpoint.
 *
 * Do NOT assume the literal '_library'. Tenants seeded before the system-id
 * convention landed carry md5 root ids instead — popart's Library root is
 * 'c9d61c23620dd871a6e63c030de981a5' — and comparing against the literal made
 * every folder in those tenants look like it was outside the Library, which
 * silently removed Publish Folder from the whole tenant.
 *
 * LIB lists more than the content root: '_search' is virtual, and the recycle
 * bin and UGC folder are roots too (a fresh tenant reads ['_library',
 * '_search', '_recycle', '_ugc']). Only the root carrying folder type 'root'
 * is the Library proper — taking all of them would make UGC folders publishable.
 *
 * Falls back to the literal when no endpoint list is supplied, which keeps the
 * older two-argument callers working.
 */
export const getLibraryRootIds = (endpoints, allFolders) => {
  const list = endpoints || []
  const lib = list.filter((ep) => ep && ep.id === 'LIB' && !ep.deleted)[0]

  if (!lib || !lib.rootFolders || lib.rootFolders.length === 0) {
    return ['_library']
  }

  const roots = lib.rootFolders.filter((folderId) => folderId !== '_search')

  if (!allFolders) return roots

  const contentRoots = roots.filter((folderId) => {
    const folder = allFolders[folderId]
    return folder && folder.type === ROOT_FOLDER_TYPE
  })

  return contentRoots.length > 0 ? contentRoots : roots
}

/*
 * Exception function.  Validates whether a folder is outside the Library
 * endpoint by walking the parentId chain to its root folder and checking that
 * root against the LIB endpoint's own rootFolders.
 */
export const isNotLibraryFolder = (
  folderId,
  allFolders,
  constants,
  folderType,
  endpoints,
) => {
  const folder = allFolders[folderId]
  if (!folder) return true

  const root = getRootOfNode(folder, allFolders)

  return getLibraryRootIds(endpoints, allFolders).indexOf(root) < 0
}

/*
 * Whether a folder, or any subfolder beneath it, still lists an asset. In a
 * published endpoint a folder's `assets` only holds assets that are live or
 * pending there — unpublishing, rejecting, or approving a removal drops the
 * id from the list — so a non-empty list anywhere in the tree means the tree
 * is still published.
 */
export const treeHasAssets = (folderId, allFolders, visited = {}) => {
  const folder = allFolders[folderId]
  if (!folder || folder.deleted || visited[folderId]) return false
  visited[folderId] = true
  if ((folder.assets || []).length > 0) return true
  return (folder.childFolders || []).some((childId) =>
    treeHasAssets(childId, allFolders, visited),
  )
}

/*
 * Exception function.  Validates whether a folder is a published-endpoint
 * folder whose tree still contains published or pending assets. Such a folder
 * cannot be deleted (the move-to-recycle-bin saga refuses it), so the menu
 * offers Unpublish Folder in place of Delete Folder.
 */
export const isPublishedFolder = (
  folderId,
  allFolders,
  constants,
  folderType,
  endpoints,
) => {
  if (
    isNotLibraryFolder(
      folderId,
      allFolders,
      constants,
      folderType,
      endpoints,
    ) === false
  )
    return false
  return treeHasAssets(folderId, allFolders)
}

/*
 * Exception function.  The inverse of isPublishedFolder, for the Unpublish
 * Folder action.
 */
export const isNotPublishedFolder = (...args) => !isPublishedFolder(...args)
